import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { T, useT } from '@transifex/react';
import { useCompany } from 'src/app/Company.store';
import { apiClient, apiClientMethods } from 'src/api/client';
import { resolveHaulerCompanyFromEmail } from 'src/api/devMock';
import {
  SS_HAULER_COMPANY,
  SS_HAULER_EMAIL,
  SS_HAULER_MODE,
} from 'src/api/state';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

// Mock OTP code: the backend endpoints don't exist yet, so the verify step
// matches against this constant. Swap requestOtp + verifyOtp for real
// `apiClient.post(...)` calls once the backend ships them.
const MOCK_OTP_CODE = '123456';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function requestOtp(email: string): Promise<void> {
  await sleep(400);
  console.info('[mock OTP]', email, '→ code', MOCK_OTP_CODE);
}

async function verifyOtp(code: string): Promise<void> {
  await sleep(200);
  if (code !== MOCK_OTP_CODE) throw new Error('Invalid code');
}

const emailSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
});
type EmailValues = z.infer<typeof emailSchema>;

export const EmailOtpLogin: React.FC = () => {
  const tx = useT();
  const { hostname } = useCompany();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const emailForm = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const onRequest = async (values: EmailValues) => {
    setSubmitError(null);
    try {
      await requestOtp(values.email);
      setEmail(values.email);
      setCode('');
      setCodeError(null);
      setStep('code');
    } catch (e: $TSFixMe) {
      setSubmitError(e?.message || tx('Failed to send code'));
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError(null);
    if (!/^\d{6}$/.test(code)) {
      setCodeError(tx('Code must be 6 digits'));
      return;
    }
    setIsVerifying(true);
    try {
      await verifyOtp(code);
      // Mock path until backend ships an OTP endpoint that returns a real
      // hauler-scoped token: trigger the existing tenant auto-login to obtain
      // an anonymous-but-valid token, persist the hauler-mode flag, and
      // reload. UserStoreProvider's augmentUser will then upgrade the
      // anonymous user to user_type='hauler' on the next paint.
      const { data } = await apiClient.post(`company/${hostname}/login`, {
        username: '',
        password: '',
      });
      if (!data?.token) throw new Error('No token returned');
      apiClientMethods.setToken(data.token);
      localStorage.setItem('AUTH_TOKEN', data.token);
      // Hauler-session flags live in sessionStorage so they die with the
      // tab and never leak into a citizen flow opened in another window.
      sessionStorage.setItem(SS_HAULER_MODE, 'true');
      sessionStorage.setItem(SS_HAULER_EMAIL, email);
      // Tag the session with the hauler company resolved from the email
      // domain so the devMock layer can return tenant-scoped fixtures.
      // Unknown emails get 'unknown' — login still works but no scoped data.
      sessionStorage.setItem(
        SS_HAULER_COMPANY,
        resolveHaulerCompanyFromEmail(email) ?? 'unknown'
      );
      window.location.assign('/hauler/properties');
    } catch {
      setCodeError(tx('Invalid code'));
      setIsVerifying(false);
    }
  };

  if (step === 'email') {
    return (
      <div className="wh-ui w-full">
        <Form {...emailForm}>
          <form
            onSubmit={emailForm.handleSubmit(onRequest)}
            className="flex w-full flex-col gap-4"
          >
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <T _str="Email" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder={tx('you@example.com')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submitError && (
              <p className="text-sm text-[var(--text-danger)]">{submitError}</p>
            )}
            <Button
              type="submit"
              disabled={emailForm.formState.isSubmitting}
              className="h-12 w-full"
            >
              {emailForm.formState.isSubmitting ? (
                <T _str="Sending code…" />
              ) : (
                <T _str="Send code" />
              )}
            </Button>
          </form>
        </Form>
      </div>
    );
  }

  return (
    <div className="wh-ui w-full">
      <form onSubmit={onVerify} className="flex w-full flex-col gap-4">
        <div className="grid gap-2">
          <label
            htmlFor="hauler-otp-code"
            className="text-sm font-medium leading-none"
          >
            <T _str="Enter the 6-digit code sent to {email}" email={email} />
          </label>
          <Input
            id="hauler-otp-code"
            type="text"
            autoComplete="off"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (codeError) setCodeError(null);
            }}
          />
          {codeError && <p className="text-sm text-destructive">{codeError}</p>}
        </div>
        <p className="text-xs text-muted-foreground">
          {/* Demo aid, not a product string — intentionally not wrapped in <T> so it
              isn't extracted/translated. Remove when the real backend OTP goes live. */}
          {`Backend OTP isn't live yet — for now use code ${MOCK_OTP_CODE}.`}
        </p>
        <Button type="submit" disabled={isVerifying} className="h-12 w-full">
          {isVerifying ? <T _str="Verifying…" /> : <T _str="Verify" />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setStep('email');
            setSubmitError(null);
            setCode('');
            setCodeError(null);
          }}
          className="h-12 w-full"
        >
          <T _str="Use a different email" />
        </Button>
      </form>
    </div>
  );
};

export default EmailOtpLogin;
